'use client';

import './tab-group.scss';

import * as Tabs from '@radix-ui/react-tabs';
import { t } from 'i18next';
import { ElementType, useEffect, useState } from 'react';
import { z } from 'zod';

import { trackClick } from '@sola/cookies/components/datalayer.js';
import { FlexibleContentBaseProps } from '@sola/schema/lib/schema.js';

import { FieldIcon } from '../field/field';

const TabGroupFlexibleContentBaseProps = FlexibleContentBaseProps.extend({
  tabKey: z.string(),
});

export type TabGroupProps = {
  title?: string;
  activeValue?: string;
  items?: Array<z.infer<typeof TabGroupFlexibleContentBaseProps>>;
  onChange?: (value: string) => void;
};

export const TabGroup: React.FC<TabGroupProps> = ({
  title,
  activeValue,
  onChange,
  items,
}) => {
  const [activeTabItem, setActiveTabItem] = useState<string | undefined>(
    activeValue,
  );

  useEffect(() => {
    setActiveTabItem(activeValue);
  }, [activeValue]);

  return (
    items &&
    items.length > 0 && (
      <Tabs.Root
        className="TabGroup"
        defaultValue={items[0].title}
        value={activeTabItem}
        onValueChange={(value) => {
          trackClick(value);
          setActiveTabItem(value);
          if (onChange) {
            onChange(value);
          }
        }}
      >
        <Tabs.List
          className="TabGroup__list gap-comfy-md"
          aria-label={title || t('skippy.tabGroup.defaultAriaLabel')}
        >
          {items.map((item) => {
            const Comp = (
              item.seoLevel ? `h${item.seoLevel}` : 'span'
            ) as ElementType;
            return (
              <Tabs.Trigger
                className="TabGroup__buttons label-potent label-lg pr-snug-md pb-comfy-xs pl-snug-md"
                value={item.tabKey}
                key={item.title}
              >
                {item.icon && (
                  <FieldIcon iconName={item.icon} className="TabGroup__icon" />
                )}
                <Comp className="label-potent label-lg">{item.title}</Comp>
              </Tabs.Trigger>
            );
          })}
        </Tabs.List>
        {items.map((item) => (
          <Tabs.Content
            className="TabGroup__content pt-comfy-lg @lg:pt-comfy-xl"
            value={item.tabKey}
            key={item.title}
          >
            {item.body}
          </Tabs.Content>
        ))}
      </Tabs.Root>
    )
  );
};
